import { RoleFragment } from '../gql'
import { OrgData } from './OrgData'

// Links view (#107, phase 4): a role citing another one with a "#" mention in
// its texts. The editor writes a mention as [#Name](rolebase://role/<id>); a
// person is rolebase://member/<id> and is not a link between roles.
//
// 🔑 Despite the "role/" in the scheme, the id is the id of a CIRCLE — the role
// at its place in the org chart, which is what a mention points at (checked on
// the ten mentions of gouv, 22/09/2026). A role carried by several circles (a
// base role) is therefore cited circle by circle.
const ROLE_MENTION = /\[#([^\]]*)\]\(rolebase:\/\/role\/([0-9a-fA-F-]{36})\)/g

// The texts of a role where a mention can be written
const TEXTS = [
  'purpose',
  'domain',
  'accountabilities',
  'checklist',
  'indicators',
  'notes',
] as const

export interface RoleLink {
  // The circle whose text carries the mention
  fromId: string
  // The circle it cites
  toId: string
}

// Every "A cites B" of an org, read from the roles' own texts. Citing oneself
// is not a link, and the same mention twice is one link.
//
// A mention carries both an id and the name it was written with. When the id
// points nowhere — the copies of the UdN orgs on gouv gave their circles new
// ids without rewriting the mentions — the name takes over, unless it names
// several circles: nothing would say which one was meant.
export function extractRoleLinks(
  org: OrgData,
  roles: readonly RoleFragment[]
): RoleLink[] {
  const byName = new Map<string, string | null>()
  for (const circle of org.circles) {
    const name = (org.getRole(circle.roleId)?.name ?? '').trim().toLowerCase()
    if (!name) continue
    byName.set(name, byName.has(name) ? null : circle.id)
  }
  const seen = new Set<string>()
  const links: RoleLink[] = []

  for (const role of roles) {
    const text = TEXTS.map((key) => role[key] ?? '').join('\n')
    const mentions = [...text.matchAll(ROLE_MENTION)]
    if (mentions.length === 0) continue

    // The circles carrying this role: its texts belong to all of them
    const fromCircles = org.circles.filter(
      (circle) => circle.roleId === role.id
    )
    for (const [, name, mentionId] of mentions) {
      const toId = org.getCircle(mentionId)
        ? mentionId
        : byName.get(name.trim().toLowerCase()) ?? undefined
      if (!toId) continue
      for (const from of fromCircles) {
        const key = `${from.id}>${toId}`
        if (toId === from.id || seen.has(key)) continue
        seen.add(key)
        links.push({ fromId: from.id, toId })
      }
    }
  }
  return links
}
